import { CreateOrganization, useOrganization } from "@clerk/clerk-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Crown, ArrowRight, Loader2, Users } from "lucide-react";

export default function CreateOrganizationPage() {
  const { organization } = useOrganization();
  const syncClerkOrganization = useMutation(api.organizations.syncClerkOrganization);
  const convertWorkspace = useMutation(api.personalWorkspaces.convertToOrganization);
  const canCreateOrg = useQuery(api.subscriptions.canCreateOrganization);
  const navigate = useNavigate();

  // Sync organization with local database when Clerk organization is created
  useEffect(() => {
    if (organization) {
      const syncOrganization = async () => {
        try {
          // Try to convert personal workspace data to organization
          try {
            await convertWorkspace({
              clerkOrgId: organization.id,
              clerkOrgName: organization.name,
              clerkOrgSlug: organization.slug || undefined,
            });
          } catch {
            // If no personal workspace exists, just sync the Clerk organization
            await syncClerkOrganization({
              clerkOrgId: organization.id,
              clerkOrgName: organization.name,
              clerkOrgSlug: organization.slug || undefined,
            });
          }
          // Redirect to dashboard after successful sync
          navigate("/dashboard");
        } catch (error) {
          console.error("Failed to sync organization:", error);
          // Still redirect to dashboard, user can fix organization later
          navigate("/dashboard");
        }
      };

      syncOrganization();
    }
  }, [organization, convertWorkspace, syncClerkOrganization, navigate]);

  // Show loading while checking subscription
  if (canCreateOrg === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If user already has an org, redirect to dashboard
  if (canCreateOrg.alreadyInOrg) {
    navigate("/dashboard");
    return null;
  }

  // Show upgrade prompt if user can't create organization
  if (!canCreateOrg.canCreate) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
              <Crown className="h-7 w-7 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Upgrade to Create a Team</CardTitle>
            <CardDescription className="text-base">
              Create a team organization to invite colleagues and collaborate together.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="mb-3 flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">Team organizations include:</span>
              </div>
              <ul className="ml-8 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Invite and manage team members
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Assign roles and permissions
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Share bids, clients, and documents
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Collaborate on proposals in real-time
                </li>
              </ul>
            </div>
            <Button asChild className="h-12 w-full text-base font-medium">
              <Link to="/settings/billing">
                View Upgrade Options
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" asChild className="w-full">
              <Link to="/dashboard">Continue as Individual</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show Clerk's organization creation UI for paid users
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Create your team</h2>
          <p className="mt-2 text-gray-600">Set up your organization to invite team members</p>
        </div>
        <CreateOrganization
          afterCreateOrganizationUrl="/create-organization"
          skipInvitationScreen={true}
        />
      </div>
    </div>
  );
}
